/**
 * Get Command
 *
 * Retrieve a single credential value for programmatic use.
 */

import { CredentialService } from '../core/service';
import * as output from '../utils/output';

interface GetOptions {
  raw?: boolean;
  json?: boolean;
  quiet?: boolean;
}

export async function getCommand(name: string, options: GetOptions): Promise<void> {
  const service = new CredentialService();

  // Check vault exists
  const vaults = await service.hasVault();
  if (!vaults.local && !vaults.global) {
    if (!options.quiet && !options.raw) {
      output.error('No vault found. Run `secretsage init` first.');
    }
    process.exit(1);
  }

  await service.init();

  try {
    const credential = await service.get(name);

    if (!credential) {
      if (!options.quiet && !options.raw) {
        output.error(`Credential "${name}" not found.`);
      }
      process.exit(1);
    }

    if (options.raw) {
      // Raw output: just the value, no newline (for scripting/piping)
      process.stdout.write(credential.value);
    } else if (options.json) {
      // JSON output
      console.log(JSON.stringify({ name, value: credential.value }));
    } else {
      // Default mode: human-readable "  KEY: value" form via keyValue formatter.
      // Unsafe for piping — the rendered prefix bytes will be included in pipe output.
      // When stdout is a pipe (non-TTY), warn on stderr and recommend --raw.
      // Default behavior is unchanged in v0.5.0; will change to cat-equivalent in v1.0.
      if (!process.stdout.isTTY && !options.quiet) {
        process.stderr.write(
          `secretsage: warning — default 'get' mode is unsafe to pipe (renders labeled form). ` +
          `Use 'secretsage get ${name} --raw' for safe pipe output. ` +
          `This default will change in v1.0.\n`
        );
      }
      output.keyValue(name, credential.value);
    }
  } catch (error) {
    if (!options.quiet && !options.raw) {
      output.error((error as Error).message);
    }
    process.exit(1);
  }
}
